import {
  type ListNotificationPreferencesResponse,
  type ListNotificationsQuery,
  type ListNotificationsResponse,
  listNotificationPreferencesResponseSchema,
  listNotificationsResponseSchema,
  type MarkAllReadResponse,
  markAllReadResponseSchema,
  type NotificationCategory,
  type NotificationPreferenceRow,
  notificationPreferenceRowSchema,
  type OkResponse,
  okResponseSchema,
  type UnreadCountResponse,
  type UpdateNotificationPreference,
  unreadCountResponseSchema,
} from "@/features/notifications/schemas/notification.schema";
import { apiCall } from "@/shared/lib/api-call";
import { apiPaths } from "@/shared/lib/api-paths";

/// HTTP boundary for Notifications. Per webui T3 / FE-SEC6 every response
/// is Zod-parsed via the shared `apiCall` helper.

function toListQs(query: ListNotificationsQuery): string {
  const params = new URLSearchParams();
  if (query.status) params.set("status", query.status);
  if (query.category) params.set("category", query.category);
  if (query.since) params.set("since", query.since);
  if (query.cursor) params.set("cursor", query.cursor);
  if (query.limit !== undefined) params.set("limit", String(query.limit));
  const qs = params.toString();
  return qs ? `?${qs}` : "";
}

export async function listNotifications(
  workspaceId: string,
  query: ListNotificationsQuery = {},
): Promise<ListNotificationsResponse> {
  return apiCall<ListNotificationsResponse>(
    `${apiPaths.notifications.list(workspaceId)}${toListQs(query)}`,
    { method: "GET" },
    listNotificationsResponseSchema,
  );
}

export async function getUnreadNotificationCount(
  workspaceId: string,
): Promise<UnreadCountResponse> {
  return apiCall<UnreadCountResponse>(
    apiPaths.notifications.unreadCount(workspaceId),
    { method: "GET" },
    unreadCountResponseSchema,
  );
}

export async function markNotificationRead(
  workspaceId: string,
  notificationId: string,
): Promise<OkResponse> {
  return apiCall<OkResponse>(
    apiPaths.notifications.read(workspaceId, notificationId),
    { method: "PATCH", body: JSON.stringify({}) },
    okResponseSchema,
  );
}

export async function markAllNotificationsRead(
  workspaceId: string,
  body: { category?: NotificationCategory } = {},
): Promise<MarkAllReadResponse> {
  return apiCall<MarkAllReadResponse>(
    apiPaths.notifications.markAllRead(workspaceId),
    { method: "POST", body: JSON.stringify(body) },
    markAllReadResponseSchema,
  );
}

export async function softDeleteNotification(
  workspaceId: string,
  notificationId: string,
): Promise<OkResponse> {
  return apiCall<OkResponse>(
    apiPaths.notifications.detail(workspaceId, notificationId),
    { method: "DELETE" },
    okResponseSchema,
  );
}

export async function listNotificationPreferences(
  workspaceId: string,
): Promise<ListNotificationPreferencesResponse> {
  return apiCall<ListNotificationPreferencesResponse>(
    apiPaths.notifications.preferences(workspaceId),
    { method: "GET" },
    listNotificationPreferencesResponseSchema,
  );
}

export async function updateNotificationPreference(
  workspaceId: string,
  body: UpdateNotificationPreference,
): Promise<NotificationPreferenceRow> {
  return apiCall<NotificationPreferenceRow>(
    apiPaths.notifications.preferences(workspaceId),
    { method: "PATCH", body: JSON.stringify(body) },
    notificationPreferenceRowSchema,
  );
}

// 2026-05-21: delivery-audit endpoint removed.
